import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profiles`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests when available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Profile API Error:', error.response?.data || error.message);
    throw error;
  }
);

export interface StudentProfile {
  id: string;
  displayName: string;
  email: string;
  avatar?: string;
  bio?: string;
  location?: string;
  phone?: string;
  website?: string;
  skills: string[];
  interests: string[];
  education: {
    institution: string;
    degree: string;
    field: string;
    graduationYear: number;
    gpa?: number;
  }[];
  experience: {
    company: string;
    position: string;
    startDate: string;
    endDate?: string;
    description: string;
  }[];
  languages: {
    language: string;
    proficiency: 'Beginner' | 'Intermediate' | 'Advanced' | 'Native';
  }[];
  certifications: {
    name: string;
    issuer: string;
    date: string;
    credentialId?: string;
  }[];
  projects?: {
    name: string;
    description: string;
    technologies: string[];
    url?: string;
    githubUrl?: string;
  }[];
  portfolio?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  availability: 'full-time' | 'part-time' | 'contract' | 'internship';
  expectedSalary?: {
    min: number;
    max: number;
    currency: string;
  };
  preferredJobTypes: string[];
  preferredLocations: string[];
  remotePreference: 'onsite' | 'remote' | 'hybrid';
  courseStats: {
    enrolled: number;
    completed: number;
    inProgress: number;
    averageScore: number;
  };
  jobApplications: {
    total: number;
    pending: number;
    interviews: number;
    offers: number;
  };
  skillRatings: {
    skill: string;
    rating: number;
    endorsements: number;
  }[];
  overallRanking?: number;
  createdAt: any;
  updatedAt: any;
}

export interface EmployerProfile {
  id: string;
  companyName: string;
  displayName: string;
  email: string;
  companyLogo?: string;
  description?: string;
  industry: string;
  companySize: string;
  website?: string;
  location?: string;
  phone?: string;
  foundedYear?: number;
  employeeCount?: number;
  socialMedia: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
  };
  companyBenefits: string[];
  companyValues: string[];
  departments: string[];
  jobPostingStats: {
    activeJobs: number;
    totalJobs: number;
    totalApplications: number;
    averageApplicationsPerJob: number;
    hiredCandidates: number;
  };
  hiringStats: {
    averageTimeToHire: number;
    successRate: number;
    candidateRating: number;
    repeatApplicants: number;
  };
  subscriptionTier: 'basic' | 'premium' | 'enterprise';
  verificationStatus: 'pending' | 'verified' | 'rejected';
  createdAt: any;
  updatedAt: any;
}

class ProfileService {
  // Get current user's profile
  async getCurrentProfile(): Promise<StudentProfile | EmployerProfile> {
    const response = await api.get('/me');
    return response.data.data;
  }

  // Update current user's profile
  async updateProfile(updates: Partial<StudentProfile> | Partial<EmployerProfile>): Promise<StudentProfile | EmployerProfile> {
    const response = await api.put('/me', updates);
    return response.data.data;
  }

  // Get student profile by ID (public)
  async getStudentProfile(userId: string): Promise<Partial<StudentProfile>> {
    const response = await api.get(`/student/${userId}`);
    return response.data.data;
  }

  // Get employer profile by ID (public)
  async getEmployerProfile(userId: string): Promise<Partial<EmployerProfile>> {
    const response = await api.get(`/employer/${userId}`);
    return response.data.data;
  }

  // Refresh profile statistics
  async refreshStats(): Promise<StudentProfile | EmployerProfile> {
    const response = await api.post('/me/refresh-stats');
    return response.data.data;
  }

  // Update profile avatar/logo
  async updateAvatar(avatarUrl: string): Promise<StudentProfile | EmployerProfile> {
    const response = await api.post('/me/avatar', { avatarUrl });
    return response.data.data;
  }

  // Update company logo specifically for employers
  async updateCompanyLogo(logoUrl: string): Promise<EmployerProfile> {
    const response = await api.put('/me', { companyLogo: logoUrl });
    return response.data.data;
  }

  // Validation helpers
  validateStudentProfile(profile: Partial<StudentProfile>): string[] {
    const errors: string[] = [];

    if (!profile.displayName?.trim()) {
      errors.push('Display name is required');
    }

    if (!profile.email?.trim()) {
      errors.push('Email is required');
    }

    if (profile.email && !/\S+@\S+\.\S+/.test(profile.email)) {
      errors.push('Please enter a valid email address');
    }

    if (profile.phone && !/^[\+]?[\s\d\-\(\)]{10,}$/.test(profile.phone)) {
      errors.push('Please enter a valid phone number');
    }

    if (profile.website && !profile.website.startsWith('http')) {
      errors.push('Website URL must start with http:// or https://');
    }

    return errors;
  }

  validateEmployerProfile(profile: Partial<EmployerProfile>): string[] {
    const errors: string[] = [];

    if (!profile.companyName?.trim()) {
      errors.push('Company name is required');
    }

    if (!profile.displayName?.trim()) {
      errors.push('Display name is required');
    }

    if (!profile.email?.trim()) {
      errors.push('Email is required');
    }

    if (profile.email && !/\S+@\S+\.\S+/.test(profile.email)) {
      errors.push('Please enter a valid email address');
    }

    if (profile.phone && !/^[\+]?[\s\d\-\(\)]{10,}$/.test(profile.phone)) {
      errors.push('Please enter a valid phone number');
    }

    if (profile.website && !profile.website.startsWith('http')) {
      errors.push('Website URL must start with http:// or https://');
    }

    if (!profile.industry?.trim()) {
      errors.push('Industry is required');
    }

    if (!profile.companySize?.trim()) {
      errors.push('Company size is required');
    }

    return errors;
  }

  // Helper method to format profile data for display
  formatProfileForDisplay(profile: StudentProfile | EmployerProfile): any {
    return {
      ...profile,
      createdAt: profile.createdAt?.toDate ? profile.createdAt.toDate() : new Date(profile.createdAt),
      updatedAt: profile.updatedAt?.toDate ? profile.updatedAt.toDate() : new Date(profile.updatedAt)
    };
  }
}

export const profileService = new ProfileService();
export default profileService;